import { TDataset } from '../../../models/aibuilder/aibuilder';
import { ImageModel } from './useImageModel';
import { TextModel } from './useTextModel';
import { loadImage } from './loadImage';

export const MAX_SELF_CHECK_ISSUES = 5;

export async function runImageSelfCheck(dataset: TDataset, imageModel: ImageModel): Promise<string[]> {
  const labels = dataset.labels || [];
  const labelNameById = new Map(labels.map((label) => [label.id, label.name]));
  const samples = (dataset.samples || []).filter(
    (sample) => !sample.excluded && sample.label_id != null && sample.content_url,
  );

  const messages: string[] = [];
  let total = 0;
  const positionByLabel = new Map<number, number>();

  for (const sample of samples) {
    const labelId = sample.label_id as number;
    const labelName = labelNameById.get(labelId);
    if (!labelName) continue;
    const position = (positionByLabel.get(labelId) || 0) + 1;
    positionByLabel.set(labelId, position);
    try {
      const image = await loadImage(sample.content_url as string);
      const results = await imageModel.classify(image);
      const predicted = results[0]?.label;
      if (predicted && predicted !== labelName) {
        total += 1;
        if (messages.length < MAX_SELF_CHECK_ISSUES) {
          messages.push(
            `La ${position}ᵉ image du label "${labelName}" ressemble plutôt à "${predicted}" pour ton IA. Vérifie qu'elle est bien liée à "${labelName}".`,
          );
        }
      }
    } catch {
      // Skip a sample that fails to load/classify; the rest of the check still runs.
    }
  }

  if (total > MAX_SELF_CHECK_ISSUES) {
    messages.push(
      `Et ${total - MAX_SELF_CHECK_ISSUES} autre(s) exemple(s) ressemble(nt) aussi à un autre label pour ton IA.`,
    );
  }

  return messages;
}

export async function runTextSelfCheck(dataset: TDataset, textModel: TextModel): Promise<string[]> {
  const labels = dataset.labels || [];
  const labelNameById = new Map(labels.map((label) => [label.id, label.name]));
  const samples = (dataset.samples || []).filter(
    (sample) => !sample.excluded && sample.label_id != null && sample.content_text,
  );

  const messages: string[] = [];
  let total = 0;

  for (const sample of samples) {
    const labelName = labelNameById.get(sample.label_id as number);
    if (!labelName) continue;
    try {
      const results = await textModel.classify(sample.content_text as string);
      const predicted = results[0]?.label;
      if (predicted && predicted !== labelName) {
        total += 1;
        if (messages.length < MAX_SELF_CHECK_ISSUES) {
          messages.push(
            `La phrase "${sample.content_text}" du label "${labelName}" ressemble plutôt à "${predicted}" pour ton IA. Vérifie qu'elle est bien liée à "${labelName}".`,
          );
        }
      }
    } catch {
      // Skip a sample that fails to classify; the rest of the check still runs.
    }
  }

  if (total > MAX_SELF_CHECK_ISSUES) {
    messages.push(
      `Et ${total - MAX_SELF_CHECK_ISSUES} autre(s) exemple(s) ressemble(nt) aussi à un autre label pour ton IA.`,
    );
  }

  return messages;
}
